import { useEffect, useState } from "react";
import { useNavigate } from "@excellent/ui/router";
import { PlayDriver } from "@excellent/ui";
import { useLiveResource, notifyLive } from "@excellent/ui/api/live";
import { apiCall } from "@excellent/ui/api";
import { play, type PlayActionDTO, type PlaySignal } from "@excellent/ui/api/contracts/play";
import { resolvePlayRoute, playHasObject } from "@/modes/play-route";
import { postPlaySignal } from "../../shared/chrome/play-signal";

/**
 * PlayStagePane — Play as a Stage occupant (docs/design/UI-UX-PATTERNS-PRD.md
 * Issue 22). This is the SAME ranked-queue + driver wiring `play-dialog.tsx`
 * owns, lifted out of the Radix `<Dialog fullscreen>` takeover so it renders
 * INSIDE a Stage tab (`StageHost`): the app chrome stays visible around the
 * `inset-[10px]` panel, and Play inherits the Stage's minimize/close/tab-switch
 * for free. The driver (`PlayDriver`) is reused verbatim — its `PlayTakeoverShell`
 * paints the live progress (Track cue + film-strip) and the top-right options
 * (Skip / Delegate / Dive-in; the entrance lanes) inside the panel.
 *
 * The pane owns nothing durable: it fetches the live `play.queue`, freezes it for
 * the drive (so background re-ranks don't reshuffle the cards under the user),
 * and posts answers/signals through the same seams the dialog used. `onExhausted`
 * closes the occupant's tab (handed down by the host).
 *
 * `active` gates the drive: only the active tab feeds the driver its cards, so a
 * backgrounded Play tab (another app in front) neither re-ranks nor grabs focus.
 */
export function PlayStagePane({
  startActionKey,
  active,
  onExhausted,
}: {
  /** Open focused on one card (skips the entrance launcher). */
  startActionKey?: string;
  /** True while this is the front tab — the drive only runs when active. */
  active: boolean;
  /** Called when the user finishes / closes the drive — the host closes the tab. */
  onExhausted: () => void;
}) {
  // Seam returns { push, replace } (router-adapter), not TanStack's navigate(opts).
  const router = useNavigate();

  const { data, mutate } = useLiveResource(
    play.queue,
    {},
    { topic: "play", refreshInterval: 60_000 },
  );

  // Freeze the queue for the duration of an open drive (mirrors play-dialog.tsx):
  // every answer/decision fires a background re-rank (notifyLive('play')) that
  // must update the live data + badge WITHOUT reshuffling the cards under the
  // user. Snapshot once the tab is active with data; clear when it leaves.
  const [frozen, setFrozen] = useState<{ actions: PlayActionDTO[]; total: number } | null>(null);
  useEffect(() => {
    if (active && !frozen && data) setFrozen({ actions: data.actions, total: data.total });
    if (!active && frozen) {
      setFrozen(null);
      void mutate();
    }
  }, [active, frozen, data, mutate]);

  const actions = frozen?.actions ?? data?.actions ?? [];
  const total = frozen?.total ?? data?.total ?? 0;

  // ── the identical signal/answer/verb wiring play-dialog.tsx owns ────────────
  const signal = async (
    action: PlayActionDTO,
    sig: PlaySignal,
    reason?: string,
    fieldKey?: string,
  ) => {
    const target = action.answer?.target;
    const isQuestionTarget =
      target?.kind === "project-question" || target?.kind === "section-question";
    if (sig === "skipped" && isQuestionTarget && fieldKey) {
      try {
        await apiCall(play.answer, {
          body: { actionKey: action.key, target, fieldKey, skip: true, reason },
        });
        notifyLive("play");
      } catch {
        // best-effort; fall through to the feedback signal
      }
    }
    return postPlaySignal(action, sig, reason);
  };

  const answer = async (action: PlayActionDTO, value: string, fieldKey?: string) => {
    if (!action.answer) return;
    await apiCall(play.answer, {
      body: {
        actionKey: action.key,
        target: action.answer.target,
        fieldKey: fieldKey ?? action.answer.fieldKey,
        value,
      },
    });
    notifyLive("play");
  };

  // Do/Dive-in/Delegate navigate away — closing the drive (onExhausted) then
  // routing, so the Stage tab doesn't linger behind the destination.
  const navigateTo = (action: PlayActionDTO, route: string) => {
    void postPlaySignal(action, "started");
    onExhausted();
    router.push(route);
  };
  const onDo = (action: PlayActionDTO) => navigateTo(action, resolvePlayRoute(action));
  const onDive = (action: PlayActionDTO) =>
    navigateTo(action, resolvePlayRoute(action, { dive: true }));
  const onDelegate = (action: PlayActionDTO) => {
    void postPlaySignal(action, "started");
    onExhausted();
    // /agents/goals/new is unmounted; land on the verification shell.
    router.push("/");
  };

  const fireTrigger = async (action: PlayActionDTO) => {
    if (!action.trigger) return;
    try {
      await apiCall(play.trigger, {
        body: {
          kind: action.trigger.kind,
          projectSlug: action.trigger.projectSlug,
          questionId: action.trigger.questionId,
        },
      });
      notifyLive("play");
    } catch {
      // best-effort; the worker's output surfaces on the next re-rank
    }
  };

  return (
    <div className="h-full w-full">
      <PlayDriver
        // Remount when the focused card changes so it re-seeds onto it.
        key={startActionKey ?? "all"}
        actions={actions}
        total={total}
        startActionKey={startActionKey}
        onSignal={signal}
        onAnswer={answer}
        onTrigger={fireTrigger}
        onExhausted={onExhausted}
        onDo={onDo}
        onDive={onDive}
        onDelegate={onDelegate}
        canDive={playHasObject}
      />
    </div>
  );
}
